import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import Api from "../../../api/generalApi/Api";
import { UnitInput, GeneralButton } from "../../../components";
import { Methods } from "../../../Constants/ApiConstants";
import { EndPoints } from "../../../Constants/endpoints";
import { GeneralResponse } from "../../../interface/GeneralResponse";
import { useToast } from "../../../Services/ToastContext";
import { useGetData } from "../../../hooks";
import { EditProfileLayout } from "../component";

export interface ITarrif {
  Tarrif: number | null;
}

export interface IGetTarrifResponse {
  InspectionTarrif: number | null;
}

interface EditPriceProp {
  onClose?: () => void;
  className?: string;
}

const EditPrice: React.FC<EditPriceProp> = ({ onClose, className = "" }) => {
  const navigate = useNavigate();
  const { data, error, isPending, refetch } = useGetData<IGetTarrifResponse>(
    EndPoints.GetPrice,
  );

  const { t } = useTranslation();
  const { showToast } = useToast();
  const methods = useForm<ITarrif>({
    defaultValues: {
      Tarrif: undefined,
    },
  });

  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const { handleSubmit, reset, watch } = methods;

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      isInitialLoad &&
      data &&
      data.Data &&
      data.Data.InspectionTarrif !== undefined
    ) {
      reset({ Tarrif: data.Data.InspectionTarrif ?? undefined });
      setIsInitialLoad(false);
    }
  }, [data, reset, isInitialLoad]);

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: (formData: ITarrif): Promise<GeneralResponse<ITarrif>> => {
      return Api<ITarrif, GeneralResponse<ITarrif>>({
        method: Methods.POST,
        endpoint: EndPoints.EditPrice,
        body: formData,
      });
    },
    onSuccess: (response, formData) => {
      if (response.Status) {
        refetch();
        showToast(t("price_added"), "success");
        queryClient.invalidateQueries({
          queryKey: [EndPoints.GetPersonalInfo],
        });
        queryClient.invalidateQueries({ queryKey: ["price"] });
      } else {
        throw new Error(response.ErrorMessage || "Price Error");
      }
      if (onClose) {
        onClose();
      } else {
        navigate("/profile/price");
      }
    },
    onError: (error: Error) => {
      showToast(error.message || t("failed_editing_the_price"), "error");
    },
  });

  const onSubmit: SubmitHandler<ITarrif> = (formData) => {
    mutate({ Tarrif: formData.Tarrif });
  };

  if (error) {
    return <p>{t("error_loading_data") + ": " + (error as Error).message}</p>;
  }

  return (
    <FormProvider {...methods}>
      <EditProfileLayout
        title={t("session_price")} 
        onDiscard={onClose ?? handleBack}
        onSave={() => handleSubmit(onSubmit)()}
        isSaving={isMutating}
        saveDisabled={watch("Tarrif") === data?.Data?.InspectionTarrif} 
      >
        <form
          className="flex h-full min-h-0 w-full flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isPending ? (
            <div className="h-10 w-full animate-pulse rounded-lg bg-systemBg" />
          ) : (
            <UnitInput
              type={t("currency_unit")}
              label={t("session_price")}
              name="Tarrif"
              maxLength={10}
              placeholder={t("optional")}
            />
          )}

          <button
            type="submit"
            className="hidden"
            tabIndex={-1}
            aria-hidden="true"
          />
        </form>
      </EditProfileLayout>
    </FormProvider>
  );
};

export default EditPrice;
